import React, { useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Backdrop,
  Button,
  CircularProgress,
  ClickAwayListener,
  Divider,
  InputBase,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListSubheader,
  MenuItem,
  Paper,
  Popper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SaveIcon from "@mui/icons-material/Save";
import { QRCode } from "react-qrcode-logo";
import { ExpandMore } from "@mui/icons-material";
import GoldenSwitch from "../goldenSwitch/GoldenSwitch";
import PopoverPicker from "./PopoverColorPicker";
import QrCodeLogo from "./QrCodeLogo";
import { useTranslation } from "react-i18next";
import EyeStylePicker from "./EyeStylePicker";
import { qrCodeService } from "../../services/qrCodeService";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { searchService } from "../../services";
import { useDebounce } from "../../helpers";
import ClearIcon from "@mui/icons-material/Clear";

const PREFIX = "QRCodeCreation";

const classes = {
  root: `${PREFIX}-root`,
  textInput: `${PREFIX}-textInput`,
  qrAccordionStyle: `${PREFIX}-qrAccordionStyle`,
  qrAccordionDetails: `${PREFIX}-qrAccordionDetails`,
  qrContainer: `${PREFIX}-qrContainer`,
  popoverContainer: `${PREFIX}-popoverContainer`,
  dotsStyleContainer: `${PREFIX}-dotsStyleContainer`,
  ecSelectStyle: `${PREFIX}-ecSelectStyle`,
  textfieldsContainer: `${PREFIX}-textfieldsContainer`,
  categoryTitle: `${PREFIX}-categoryTitle`,
  saveButtonStyle: `${PREFIX}-saveButtonStyle`,
  backdropStyle: `${PREFIX}-backdropStyle`,
  inputRoot: `${PREFIX}-inputRoot`,
  inputInput: `${PREFIX}-inputInput`,
  popper: `${PREFIX}-popper`,
  loader: `${PREFIX}-loader`,
  listSection: `${PREFIX}-listSection`,
  ul: `${PREFIX}-ul`,
  subheaderList: `${PREFIX}-subheaderList`,
  listItemRoot: `${PREFIX}-listItemRoot`,
  targetsContainer: `${PREFIX}-targetsContainer`,
  targetTitle: `${PREFIX}-targetTitle`,
  targetImageContainer: `${PREFIX}-targetImageContainer`,
  targetlabel: `${PREFIX}-targetlabel`,
  exparienceContainer: `${PREFIX}-exparienceContainer`,
  myDesignContainer: `${PREFIX}-myDesignContainer`,
  targetImage: `${PREFIX}-targetImage`,
  deleteTargetIcon: `${PREFIX}-deleteTargetIcon`,
};

const QRCodeCreationContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      marginBottom: "1rem",
    },
    [`& .${classes.textInput}`]: {
      marginTop: "1rem",
      width: "100%",
    },
    [`& .${classes.categoryTitle}`]: {
      display: "flex",
      flexDirection: "column",
      margin: "1rem 0 1rem 0",
      width: "100%",
    },
    [`& .${classes.qrAccordionStyle}`]: {
      marginTop: "1rem",
      width: "100%",
      maxWidth: "650px",
      margin: "1rem auto",
      backgroundColor: "#222222",
    },
    [`& .${classes.qrAccordionDetails}`]: {
      display: "flex",
      flexDirection: "column",
      padding: "0 3rem 0 3rem",
      maxWidth: "650px",
      margin: "0 auto",
      marginBottom: "1rem",
    },
    [`& .${classes.dotsStyleContainer}`]: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
    [`& .${classes.qrContainer}`]: {
      position: "-webkit-sticky",
      position: "sticky",
      top: 0,
      display: "flex",
      justifyContent: "center",
      zIndex: "20",
      paddingTop: "2rem",
      width: "calc(100% + 2rem)",
      height: "calc(270px + 4rem)",
      backgroundColor: "rgba(52, 52, 52, 0.7)",
    },
    [`& .${classes.popoverContainer}`]: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: "1rem",
      width: "50%",
    },
    [`& .${classes.ecSelectStyle}`]: {
      margin: "0.25rem 0 1rem 0",
    },
    [`& .${classes.textfieldsContainer}`]: {
      maxWidth: "650px",
      margin: "0 auto",
    },
    [`& .${classes.saveButtonStyle}`]: {
      marginTop: "1rem",
    },
    [`& .${classes.backdropStyle}`]: {
      color: "#D3AF5F",
      zIndex: "1000",
    },
    [`& .${classes.inputRoot}`]: {
      color: "inherit",
      width: "100%",
    },
    [`& .${classes.inputInput}`]: {
      backgroundColor: "rgba(52, 52, 52, 1)",
      padding: "0.25rem",
      // vertical padding + font size from searchIcon
      width: "100%",
    },
    [`& .${classes.popper}`]: {
      backgroundColor: "#424242",
      width: "95vw",
      maxWidth: 450,
      minHeight: 100,
      maxHeight: 350,
      overflow: "auto",
      "&::-webkit-scrollbar": {
        width: "5px",
        height: "5px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "rgb(181 154 79 / 58%)",
        borderRadius: "10px",
      },
    },
    [`& .${classes.loader}`]: {
      width: "100vw",
      maxWidth: 450,
      minHeight: 100,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "#424242",
    },
    [`& .${classes.listSection}`]: {
      backgroundColor: "inherit",
    },
    [`& .${classes.ul}`]: {
      backgroundColor: "inherit",
      padding: 0,
    },
    [`& .${classes.subheaderList}`]: {
      backgroundColor: "#424242",
      color: theme.palette.primary.main,
      fontSize: "20px",
    },
    [`& .${classes.listItemRoot}`]: {
      backgroundColor: "#424242",
    },
    [`& .${classes.targetsContainer}`]: {
      display: "flex",
      justifyContent: "space-between",
      width: "100%",
      marginBottom: "2rem",
    },
    [`& .${classes.targetTitle}`]: {
      marginBottom: "0.5rem",
      textAlign: "center",
      height: "20px",
      color: theme.palette.primary.main,
    },
    [`& .${classes.targetImageContainer}`]: {
      height: "200px",
    },
    [`& .${classes.targetlabel}`]: {
      marginTop: "0.5rem",
      textAlign: "center",
    },
    [`& .${classes.exparienceContainer}`]: {
      margin: "1rem 0",
      width: "48%",
      display: "flex",
      maxHeight: "250px",
      flexDirection: "column",
      justifyContent: "space-between",
      position: "relative",
    },
    [`& .${classes.myDesignContainer}`]: {
      margin: "1rem 0",
      width: "48%",
      maxHeight: "250px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      position: "relative",
    },
    [`& .${classes.targetImage}`]: {
      margin: "0 auto",
      height: "200px",
      width: "100%",
      objectFit: "contain",
    },
    [`& .${classes.deleteTargetIcon}`]: {
      position: "absolute",
      right: "1rem",
      transform: "translateY(-2px)",
      color: "#FFFFFFB3",
    },
  };
});

const QRCodeCreation = (props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [qrCodeParams, setQrCodeParams] = useState(null);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [openResult, setOpenResult] = React.useState(false);
  const [search, setSearch] = useState("");
  const [searching, setSearching] = React.useState(false);
  const [results, setResults] = useState(null);

  const oldTargetUrl = useRef(null);

  const searchRef = useRef(null);
  const debouncedSearch = useDebounce(search, 300);

  React.useMemo(() => {
    if (search && search.length > 0) {
      setSearching(true);
      searchService.search(search).then((data) => {
        setResults({ expariences: data.expariences, targets: data.targets });
        setSearching(false);
      });
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (props.creationOpen.id) {
      qrCodeService
        .getQrCodeById(props.creationOpen.id)
        .then((res) => {
          oldTargetUrl.current = res.targetUrl;
          setQrCodeParams(res);
        })
        .catch((err) => {
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else
            enqueueSnackbar(t("toasts.getQrCodeFailed"), { variant: "error" });
        });
    } else {
      setQrCodeParams(qrCodeParamsdefault());
    }
  }, []);

  function qrCodeParamsdefault() {
    return {
      targetUrl: "",
      name: "",
      qrParams: {
        qrSize: 250,
        qrBorder: 10,
        dotsStyle: "square",
        dotsColor: "#000000",
        backgroundColor: "#ffffff",
        eyeTopLeftShape: [0, 0, 0, 0],
        eyeTopRightShape: [0, 0, 0, 0],
        eyeBottomLeftShape: [0, 0, 0, 0],
        eyeColor: [
          { outer: "#000000", inner: "#000000" },
          { outer: "#000000", inner: "#000000" },
          { outer: "#000000", inner: "#000000" },
        ],
        logoUrl: "",
        logoHeight: 65,
        logoWidth: 65,
        logoOpacity: 1,
        logoBackgroundEmpty: false,
        logoPadding: 0,
        logoPaddingStyle: "square",
        ecLevel: "H",
      },
    };
  }

  function handleQrSaving() {
    if (props.creationOpen.id) {
      const tmp = { ...qrCodeParams };
      if (oldTargetUrl.current !== qrCodeParams.targetUrl) {
        const tmp2 = [...qrCodeParams.longLink];
        tmp2.push({
          longLink: qrCodeParams.targetUrl,
          createdDate: new Date().toISOString(),
          count: 0,
          dates: [],
        });
        tmp.longLink = tmp2;
      }
      qrCodeService
        .updateQrCode(tmp, props.creationOpen.id)
        .then(() => {
          qrCodeService
            .getQrCodes()
            .then((res) => {
              props.setListQrCodes(res);
            })
            .catch((err) => {
              if (err === 403)
                enqueueSnackbar(t("toasts.unauthorizedError"), {
                  variant: "error",
                });
              props.setListQrCodes([]);
            });
          enqueueSnackbar(t("QrManager.toasts.updateSuccess"), {
            variant: "success",
          });
          props.setCreationOpen({ open: false, id: null });
        })
        .catch((err) => {
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else
            enqueueSnackbar(t("QrManager.toasts.updateFail"), {
              variant: "error",
            });
        });
    } else {
      setOpenBackdrop(true);
      qrCodeService
        .createQrCode(qrCodeParams)
        .then((createdQr) => {
          qrCodeService
            .getQrCodes()
            .then((res) => {
              props.setListQrCodes(res);
            })
            .catch((err) => {
              if (err === 403)
                enqueueSnackbar(t("toasts.unauthorizedError"), {
                  variant: "error",
                });
              props.setListQrCodes([]);
            });
          enqueueSnackbar(t("QrManager.toasts.createSuccess"), {
            variant: "success",
          });
          setOpenBackdrop(false);
          setQrCodeParams(createdQr);
          props.setCreationOpen({ ...props.creationOpen, id: createdQr._id });
          if (qrCodeParams.shortLink)
            props.setCreationOpen({ open: false, id: null });
        })
        .catch((err) => {
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else
            enqueueSnackbar(t("QrManager.toasts.createQrCodeFailed"), {
              variant: "error",
            });
          setOpenBackdrop(false);
        });
    }
  }

  function updateQrParams(target, value, key, index = NaN) {
    const tmp = { ...qrCodeParams };
    if (isNaN(index) && key === "") {
      tmp[target] = value;
    } else if (isNaN(index) && key !== "") {
      const tmpObj = { ...tmp[target], [key]: value };
      tmp[target] = tmpObj;
    } else {
      const tmpObj = { ...tmp[target] };
      const arr = [...tmpObj[key]];
      arr[index] = value;
      tmp[target] = { ...tmp[target], [key]: arr };
    }
    setQrCodeParams(tmp);
  }

  const handleDotsToggle = () => {
    if (qrCodeParams.qrParams.dotsStyle === "square")
      updateQrParams("qrParams", "dots", "dotsStyle");
    else updateQrParams("qrParams", "square", "dotsStyle");
  };

  const handleCloseSearch = (event) => {
    if (searchRef.current && searchRef.current.contains(event.target)) {
      return;
    }
    setOpenResult(false);
  };

  function getListItemText(f) {
    return `${f.label}${f.target ? " / " + f.target : ""}${
      f.frameLabel ? " / " + f.frameLabel : ""
    }`;
  }

  const SearchComponent = () => {
    return (
      <ClickAwayListener onClickAway={handleCloseSearch}>
        <Popper
          open={openResult}
          anchorEl={searchRef.current}
          style={{ zIndex: 9999 }}
          popperOptions={{ placement: "bottom" }}
        >
          <QRCodeCreationContainer>
            <Paper elevation={12}>
              <div className={classes.popper}>
                <List subheader={<li />}>
                  {results && Object.keys(results).length === 0 && (
                    <Typography
                      variant="body1"
                      className={classes.noResultsSearchText}
                    >
                      {t("homePage.myDesign.noResults")}
                    </Typography>
                  )}
                  {searching ? (
                    <div className={classes.loader}>
                      <CircularProgress />
                    </div>
                  ) : (
                    results &&
                    (results.expariences || results.targets) &&
                    Object.keys(results)
                      .sort()
                      .map((key) => {
                        return (
                          <li
                            key={`section-${key}`}
                            className={classes.listSection}
                          >
                            <ul className={classes.ul}>
                              <ListSubheader className={classes.subheaderList}>
                                {`${key.toUpperCase()}`}
                              </ListSubheader>
                              {results[key].map((f, index) => {
                                return (
                                  <div
                                    key={`element-to-redirect-${index + 1}`}
                                    className={classes.listItemRoot}
                                  >
                                    <ListItem
                                      alignItems="flex-start"
                                      onClick={(e) => {
                                        updateQrParams(
                                          key === "expariences"
                                            ? "exparience"
                                            : "myDesign",
                                          {
                                            id: f.id,
                                            imageUrl: f.imageUrl,
                                            label: f.label,
                                          },
                                          ""
                                        );
                                        handleCloseSearch(e);
                                      }}
                                    >
                                      <ListItemAvatar>
                                        <Avatar
                                          alt={`design-${f.label}`}
                                          src={
                                            f.imageUrl
                                              ? f.imageUrl
                                              : f.imageBorderlessUrl
                                          }
                                        />
                                      </ListItemAvatar>
                                      <ListItemText
                                        primary={getListItemText(f)}
                                        className="search-result"
                                      />
                                    </ListItem>
                                    <Divider />
                                  </div>
                                );
                              })}
                            </ul>
                          </li>
                        );
                      })
                  )}
                </List>
              </div>
            </Paper>
          </QRCodeCreationContainer>
        </Popper>
      </ClickAwayListener>
    );
  };

  if (!qrCodeParams)
    return (
      <div className="loading-container">
        <CircularProgress size={50} />
        <Typography>{t("loaders.fetching")}</Typography>
      </div>
    );
  else
    return (
      <QRCodeCreationContainer>
        <div className={classes.root}>
          <Backdrop className={classes.backdropStyle} open={openBackdrop}>
            <CircularProgress color="inherit" />
          </Backdrop>
          {qrCodeParams.shortLink && (
            <div className={classes.qrContainer}>
              <QRCode
                id="react-qrcode-logo"
                value={qrCodeParams.shortLink}
                enableCORS={true}
                size={
                  qrCodeParams.qrParams.qrSize > 250
                    ? 250
                    : qrCodeParams.qrParams.qrSize
                }
                quietZone={qrCodeParams.qrParams.qrBorder}
                ecLevel={qrCodeParams.qrParams.ecLevel}
                bgColor={qrCodeParams.qrParams.backgroundColor} // black default
                fgColor={qrCodeParams.qrParams.dotsColor} //golden default
                includeMargin={true}
                qrStyle={qrCodeParams.qrParams.dotsStyle}
                logoImage={qrCodeParams.qrParams.logoUrl}
                logoHeight={qrCodeParams.qrParams.logoHeight || null}
                logoWidth={qrCodeParams.qrParams.logoWidth || null}
                logoPadding={qrCodeParams.qrParams.logoPadding}
                logoPaddingStyle={qrCodeParams.qrParams.logoPaddingStyle}
                eyeRadius={[
                  qrCodeParams.qrParams.eyeTopLeftShape,
                  qrCodeParams.qrParams.eyeTopRightShape,
                  qrCodeParams.qrParams.eyeBottomLeftShape,
                ]}
                eyeColor={qrCodeParams.qrParams.eyeColor}
                logoOpacity={
                  qrCodeParams.qrParams.logoOpacity
                    ? qrCodeParams.qrParams.logoOpacity
                    : null
                }
                removeQrCodeBehindLogo={
                  qrCodeParams.qrParams.logoBackgroundEmpty
                }
                // the higher the opacity is, the lower the logo has to be
              />
            </div>
          )}
          <div className={classes.textfieldsContainer}>
            {qrCodeParams.shortLink && (
              <>
                <Divider sx={{ margin: "1rem 0" }} />
                <Typography variant="body1" style={{ marginBottom: "0.5rem" }}>
                  {t("QrManager.linkTargets")}
                </Typography>
                <InputBase
                  placeholder={t("topMenu.searchBar")}
                  value={search}
                  onChange={(e) => {
                    setOpenResult(true);
                    setSearch(e.target.value);
                  }}
                  onFocus={() => {
                    setOpenResult(true);
                  }}
                  classes={{
                    root: classes.inputRoot,
                    input: classes.inputInput,
                  }}
                  inputProps={{ "aria-label": "search" }}
                  ref={searchRef}
                />
                {(search.length > 0 ||
                  (results &&
                    search.length === 0 &&
                    Object.keys(results).length > 0)) && <SearchComponent />}
              </>
            )}
            <div className={classes.targetsContainer}>
              {qrCodeParams.exparience && (
                <div className={classes.exparienceContainer}>
                  <ClearIcon
                    onClick={() => updateQrParams("exparience", null, "")}
                    className={classes.deleteTargetIcon}
                  />
                  <Typography variant="body2" className={classes.targetTitle}>
                    Exparience
                  </Typography>
                  <div className={classes.targetImageContainer}>
                    <img
                      src={qrCodeParams.exparience.imageUrl}
                      alt="exparience"
                      className={classes.targetImage}
                    />
                  </div>
                  <Typography variant="body2" className={classes.targetlabel}>
                    {qrCodeParams.exparience.label}
                  </Typography>
                </div>
              )}
              {qrCodeParams.myDesign && (
                <div className={classes.myDesignContainer}>
                  <ClearIcon
                    onClick={() => updateQrParams("myDesign", null, "")}
                    className={classes.deleteTargetIcon}
                  />
                  <Typography variant="body2" className={classes.targetTitle}>
                    MyDesign
                  </Typography>
                  <div className={classes.targetImageContainer}>
                    <img
                      src={qrCodeParams.myDesign.imageUrl}
                      alt="myDesign"
                      className={classes.targetImage}
                    />
                  </div>
                  <Typography variant="body2" className={classes.targetlabel}>
                    {qrCodeParams.myDesign.label}
                  </Typography>
                </div>
              )}
            </div>
            {qrCodeParams.shortLink && <Divider sx={{ margin: "1rem 0" }} />}
            <TextField
              id="qrcode-name"
              label={t("QrManager.name")}
              variant="outlined"
              value={qrCodeParams.name || ""}
              onChange={(e) => updateQrParams("name", e.target.value, "")}
              required
              className={classes.textInput}
            />
            <TextField
              id="qrcode-redirection"
              label={t("QrManager.redirection")}
              variant="outlined"
              value={qrCodeParams.targetUrl}
              onChange={(e) => updateQrParams("targetUrl", e.target.value, "")}
              required
              className={classes.textInput}
            />
            {qrCodeParams.shortLink && (
              <>
                <TextField
                  className={classes.textInput}
                  type="number"
                  InputProps={{ inputProps: { min: 0 } }}
                  variant="outlined"
                  label={t("QrManager.size")}
                  value={
                    qrCodeParams.qrParams.qrSize
                      ? qrCodeParams.qrParams.qrSize
                      : ""
                  }
                  error={!qrCodeParams.qrParams.qrSize}
                  onKeyDown={(e) => {
                    if (
                      e.key === "e" ||
                      e.key === "E" ||
                      e.key === "-" ||
                      e.key === "+"
                    ) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    updateQrParams("qrParams", e.target.value, "qrSize");
                  }}
                />
                <TextField
                  className={classes.textInput}
                  type="number"
                  InputProps={{ inputProps: { min: 0 } }}
                  variant="outlined"
                  label={t("QrManager.border")}
                  value={
                    qrCodeParams.qrParams.qrBorder
                      ? qrCodeParams.qrParams.qrBorder
                      : ""
                  }
                  error={!qrCodeParams.qrParams.qrBorder}
                  onKeyDown={(e) => {
                    if (
                      e.key === "e" ||
                      e.key === "E" ||
                      e.key === "-" ||
                      e.key === "+"
                    ) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    updateQrParams("qrParams", e.target.value, "qrBorder");
                  }}
                />
              </>
            )}
          </div>
          {qrCodeParams.shortLink && (
            <Accordion className={classes.qrAccordionStyle}>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="creation-qr-content"
                id="creation-qr-header"
              >
                <Typography>{t("QrManager.edition")}</Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.qrAccordionDetails}>
                <QrCodeLogo
                  qrCodeParams={qrCodeParams}
                  setQrCodeParams={setQrCodeParams}
                  updateQrParams={updateQrParams}
                  setOpenBackdrop={setOpenBackdrop}
                />
                <Divider sx={{ margin: "1rem 0" }} />
                <div className={classes.dotsStyleContainer}>
                  <Typography variant="body2">
                    {t("QrManager.ecLevel")}
                  </Typography>
                  <Select
                    id="ec-level-select"
                    className={classes.ecSelectStyle}
                    value={qrCodeParams.qrParams.ecLevel}
                    onChange={(e) =>
                      updateQrParams("qrParams", e.target.value, "ecLevel")
                    }
                  >
                    <MenuItem value="L">
                      {t("QrManager.errorCorrection.low")}
                    </MenuItem>
                    <MenuItem value="M">
                      {t("QrManager.errorCorrection.medium")}
                    </MenuItem>
                    <MenuItem value="Q">
                      {t("QrManager.errorCorrection.quartile")}
                    </MenuItem>
                    <MenuItem value="H">
                      {t("QrManager.errorCorrection.high")}
                    </MenuItem>
                  </Select>
                  <div className={classes.categoryTitle}>
                    <GoldenSwitch
                      checked={
                        qrCodeParams.qrParams.dotsStyle === "dots"
                          ? true
                          : false
                      }
                      onChange={() => handleDotsToggle()}
                      label={t("QrManager.dotsToggle")}
                    />
                    <GoldenSwitch
                      checked={qrCodeParams.qrParams.logoBackgroundEmpty}
                      onChange={() =>
                        updateQrParams(
                          "qrParams",
                          !qrCodeParams.qrParams.logoBackgroundEmpty,
                          "logoBackgroundEmpty"
                        )
                      }
                      label={t("QrManager.logoEmpty")}
                    />
                  </div>
                  <div className={classes.popoverContainer}>
                    <Typography variant="body1">
                      {t("QrManager.dotsColor")}
                    </Typography>
                    <PopoverPicker
                      color={qrCodeParams.qrParams.dotsColor}
                      onChange={(e) =>
                        updateQrParams("qrParams", e, "dotsColor")
                      }
                      hexColor={qrCodeParams.qrParams.dotsColor}
                      onChangeHex={(e) =>
                        updateQrParams("qrParams", e, "dotsColor")
                      }
                    />
                  </div>
                  <div className={classes.popoverContainer}>
                    <Typography variant="body1">
                      {t("QrManager.backgroundColor")}
                    </Typography>
                    <PopoverPicker
                      color={qrCodeParams.qrParams.backgroundColor}
                      onChange={(e) =>
                        updateQrParams("qrParams", e, "backgroundColor")
                      }
                      hexColor={qrCodeParams.qrParams.backgroundColor}
                      onChangeHex={(e) =>
                        updateQrParams("qrParams", e, "backgroundColor")
                      }
                    />
                  </div>
                </div>
                <Typography variant="body1" className={classes.categoryTitle}>
                  {t("QrManager.decorations")}
                </Typography>
                <EyeStylePicker
                  qrCodeParams={qrCodeParams}
                  updateQrParams={updateQrParams}
                  setQrCodeParams={setQrCodeParams}
                />
              </AccordionDetails>
            </Accordion>
          )}
          <Button
            className={classes.saveButtonStyle}
            variant="contained"
            aria-label="save-button"
            disabled={
              qrCodeParams.targetUrl && qrCodeParams.name ? false : true
            }
            startIcon={<SaveIcon />}
            onClick={() => handleQrSaving()}
          >
            {t("QrManager.save")}
          </Button>
        </div>
      </QRCodeCreationContainer>
    );
};

export default QRCodeCreation;
